import React from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Checkbox,
  ListItemText,
  IconButton,
  Link,
  Divider,
  Typography,
  Select,
} from "@mui/material";
import { FilterAlt, Clear } from "@mui/icons-material";
import MultiTagInput from "../../../../../../../common/multiTagInput";
import { QueueOrderSearchStatus } from "../../../../../../../models/queue/queueOrderSearchRequest";
import { getStatusLabel } from "../utils/statusUtils";
import Styles from "../queueOrdersReport.style";

interface SearchFormProps {
  orderNumbers: string[];
  onOrderNumbersChange: (orderNumbers: string[]) => void;
  selectedStatuses: QueueOrderSearchStatus[];
  statusDropdownOpen: boolean;
  onStatusDropdownOpenChange: (open: boolean) => void;
  onStatusChange: (event: any) => void;
  onStatusClose: () => void;
  onApplyStatusFilter: () => void;
  actionDate: string;
  onActionDateChange: (date: string) => void;
  showOnlyNullActionDate: boolean;
  onShowOnlyNullActionDateChange: (show: boolean) => void;
  filterSearch: string;
  onFilterSearchChange: (search: string) => void;
  onClearOrderNumbers: () => void;
  onClearActionDate: () => void;
  onClearFilterSearch: () => void;
}

export const SearchForm: React.FC<SearchFormProps> = ({
  orderNumbers,
  onOrderNumbersChange,
  selectedStatuses,
  statusDropdownOpen,
  onStatusDropdownOpenChange,
  onStatusChange,
  onStatusClose,
  onApplyStatusFilter,
  actionDate,
  onActionDateChange,
  showOnlyNullActionDate,
  onShowOnlyNullActionDateChange,
  filterSearch,
  onFilterSearchChange,
  onClearOrderNumbers,
  onClearActionDate,
  onClearFilterSearch,
}) => {
  return (
    <Box sx={Styles.searchFormContainer}>
      <Grid container spacing={2}>
        {/* Order Numbers Input */}
        <Grid item xs={12} md={3}>
          <Box sx={Styles.searchFieldContainer}>
            <MultiTagInput
              tags={orderNumbers}
              onTagsChange={onOrderNumbersChange}
              placeholder="e.g., 1234567-890, 1234567-891"
              label="Order Number(s)"
            />
            {orderNumbers.length > 0 && (
              <IconButton
                size="small"
                onClick={onClearOrderNumbers}
                sx={Styles.clearButton}
              >
                <Clear fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Grid>

        {/* Status Filter */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              open={statusDropdownOpen}
              onOpen={() => onStatusDropdownOpenChange(true)}
              onClose={() => {
                onStatusDropdownOpenChange(false);
                onStatusClose();
              }}
              value={selectedStatuses}
              label="Status"
              onChange={onStatusChange}
              renderValue={(selected) => {
                if (selected.includes(QueueOrderSearchStatus.All)) {
                  return "All";
                }
                return selected.map(status => getStatusLabel(status)).join(", ");
              }}
            >
              <MenuItem value={QueueOrderSearchStatus.All}>
                <Checkbox checked={selectedStatuses.includes(QueueOrderSearchStatus.All)} />
                <ListItemText primary="All" />
              </MenuItem>              
              <MenuItem value={QueueOrderSearchStatus.Scheduled}>
                <Checkbox checked={selectedStatuses.includes(QueueOrderSearchStatus.Scheduled)} />
                <ListItemText primary="Scheduled" />
              </MenuItem>
              <MenuItem value={QueueOrderSearchStatus.Pending}>
                <Checkbox checked={selectedStatuses.includes(QueueOrderSearchStatus.Pending)} />
                <ListItemText primary="Pending" />
              </MenuItem>
              <MenuItem value={QueueOrderSearchStatus.InProgress}>
                <Checkbox checked={selectedStatuses.includes(QueueOrderSearchStatus.InProgress)} />
                <ListItemText primary="In-Progress" />
              </MenuItem>
              <MenuItem value={QueueOrderSearchStatus.Done}>
                <Checkbox checked={selectedStatuses.includes(QueueOrderSearchStatus.Done)} />
                <ListItemText primary="Done" />
              </MenuItem>
              <Divider />
              <MenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  onApplyStatusFilter();
                }}
                sx={Styles.statusApplyButton}
              >
                <Link 
                  component="button"
                  variant="body2"
                  sx={Styles.statusApplyLink}
                >
                  Apply
                </Link>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Action Date */}
        <Grid item xs={12} md={3}>
          <Box>
            <TextField
              fullWidth
              label="Action Date"
              type="date"
              value={actionDate}
              onChange={(e) => onActionDateChange(e.target.value)}
              disabled={showOnlyNullActionDate}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (actionDate || showOnlyNullActionDate) && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={onClearActionDate}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={Styles.actionDateContainer}>
              <Checkbox
                size="small"
                checked={showOnlyNullActionDate}
                onChange={(e) => {
                  onShowOnlyNullActionDateChange(e.target.checked);
                  if (e.target.checked) {
                    onActionDateChange("");
                  }
                }}
                sx={{ py: 0, px: 0.5 }}
              />
              <Typography variant="caption" sx={Styles.noActionDateText}>
                No action date
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Filter Search */}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Filters"
            value={filterSearch}
            onChange={(e) => onFilterSearchChange(e.target.value)}
            placeholder='e.g., "Onshore Subpoena Chubb"'
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterAlt />
                </InputAdornment>
              ),
              endAdornment: filterSearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={onClearFilterSearch}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
